/**
 * Client-side throttling of repeated Sentry error events.
 *
 * A single persistent failure — e.g. a poller hitting the same backend error
 * every few seconds on an always-on kiosk screen — can otherwise emit tens of
 * thousands of identical events and exhaust the organisation's error quota
 * (the `getHistoricalEvents` "user not found" loop alone produced ~260K events
 * in one billing period). Reporting each distinct message once per window per
 * tab keeps the issue, its user count and its trend visible in Sentry while
 * capping the volume a single tab can generate.
 */

export const ERROR_THROTTLE_WINDOW_MS = 60 * 60 * 1000;

/**
 * Upper bound on tracked keys so long-lived tabs don't grow memory unbounded.
 * While all slots hold unexpired keys, new messages are dropped.
 */
export const ERROR_THROTTLE_MAX_KEYS = 200;

// The same RPC failure is captured both as `WebSocket error: <msg>` (socket
// `onmessage`) and as a thrown `Error: <msg>`; strip these so both variants
// share one throttle budget.
const CAPTURE_PREFIX_PATTERN = /^(?:WebSocket error|Error):\s*/;

const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export function normalizeErrorKey(message: string): string {
  return message
    .replace(CAPTURE_PREFIX_PATTERN, '')
    .replace(UUID_PATTERN, '<uuid>')
    .trim();
}

interface ErrorThrottleOptions {
  windowMs?: number;
  maxKeys?: number;
  now?: () => number;
}

/**
 * Returns a predicate that decides whether an event with the given message
 * should be sent: `true` for the first occurrence of a (normalised) message
 * in each window, `false` for repeats. Events without a message are always
 * sent since they cannot be grouped reliably.
 */
export function createErrorThrottle({
  windowMs = ERROR_THROTTLE_WINDOW_MS,
  maxKeys = ERROR_THROTTLE_MAX_KEYS,
  now = Date.now,
}: ErrorThrottleOptions = {}): (message: string) => boolean {
  const lastSentAt = new Map<string, number>();

  return (message: string) => {
    if (!message) {
      return true;
    }

    const key = normalizeErrorKey(message);
    const currentTime = now();
    const previous = lastSentAt.get(key);

    if (previous !== undefined && currentTime - previous < windowMs) {
      return false;
    }

    // Re-insert so Map iteration order stays oldest-first for pruning.
    lastSentAt.delete(key);

    if (lastSentAt.size >= maxKeys) {
      pruneExpired(lastSentAt, currentTime - windowMs);
    }

    // Never evict a key that is still inside its window: with a loop of more
    // distinct messages than `maxKeys`, each key would be evicted just before
    // it repeats and nothing would ever be throttled. Drop the overflow instead.
    if (lastSentAt.size >= maxKeys) {
      return false;
    }

    lastSentAt.set(key, currentTime);
    return true;
  };
}

/** Removes entries sent at or before `cutoff`; relies on oldest-first order. */
function pruneExpired(lastSentAt: Map<string, number>, cutoff: number) {
  for (const [key, sentAt] of lastSentAt) {
    if (sentAt > cutoff) {
      return;
    }
    lastSentAt.delete(key);
  }
}
